/**
 * Utility functions for phase progress indicators
 */

/**
 * Determines the phase index (1-7) for a phase
 * @param phase The current phase of the application
 * @returns The phase index (1-7)
 */
export const getPhaseIndex = (phase: string): number => {
  const phaseOrder = [
    "Verifizierung",
    "Deutschkenntnisse",
    "Defizitbescheid",
    "Arbeitsmarktzulassung",
    "Visum",
    "Einreise",
    "Integration"
  ];

  const phaseIndex = phaseOrder.indexOf(phase);
  return phaseIndex === -1 ? 0 : phaseIndex + 1;
};

/**
 * Gets the color for the progress indicator based on the phase
 * @param phase The current phase of the application
 * @returns The color class for the progress indicator
 */
export const getPhaseProgressColor = (phase: string): string => {
  const phaseColors: Record<string, string> = {
    Verifizierung: "text-blue-500",
    Deutschkenntnisse: "text-indigo-500",
    Defizitbescheid: "text-purple-500",
    Arbeitsmarktzulassung: "text-pink-500",
    Visum: "text-orange-500",
    Einreise: "text-amber-500",
    Integration: "text-green-500"
  };

  return phaseColors[phase] || "text-gray-500";
};

/**
 * Gets the background color for the phase badge
 * @param phase The current phase of the application
 * @returns The background color class for the phase badge
 */
export const getPhaseBadgeColor = (phase: string): string => {
  const badgeColors: Record<string, string> = {
    Verifizierung: "bg-blue-50 text-blue-700 border-blue-200",
    Deutschkenntnisse: "bg-indigo-50 text-indigo-700 border-indigo-200",
    Defizitbescheid: "bg-purple-50 text-purple-700 border-purple-200",
    Arbeitsmarktzulassung: "bg-pink-50 text-pink-700 border-pink-200",
    Visum: "bg-orange-50 text-orange-700 border-orange-200",
    Einreise: "bg-amber-50 text-amber-700 border-amber-200",
    Integration: "bg-green-50 text-green-700 border-green-200"
  };

  return badgeColors[phase] || "bg-gray-50 text-gray-700 border-gray-200";
};

/**
 * Gets the next phase in the process
 * @param currentPhase The current phase of the application
 * @returns The next phase or null if at the end
 */
export const getNextPhase = (currentPhase: string): string | null => {
  const phaseOrder = [
    "Verifizierung",
    "Deutschkenntnisse",
    "Defizitbescheid",
    "Arbeitsmarktzulassung",
    "Visum",
    "Einreise",
    "Integration"
  ];

  const currentIndex = phaseOrder.indexOf(currentPhase);

  if (currentIndex === -1 || currentIndex === phaseOrder.length - 1) {
    return null;
  }

  return phaseOrder[currentIndex + 1];
};
